/**
 * Reserved bind-source names that map to the interpolation roots
 * already exposed through inline `{{…}}` templates. Excluded from
 * `referencedSources` so they never get classified as "pending data
 * sources" and never flip the resolved `loading` flag.
 *
 * Intentionally excludes `event` and `this` — those are action-runtime
 * template roots only. Widening this set would let `_bind.*.source:
 * 'this'` pass a check that should reject it.
 *
 * Keep membership in lockstep with
 * `dynamic-flows/application/config/ReservedBindingSources.ts`
 * (duplicate literal, no shared package).
 */
export const RESERVED_BIND_SOURCES: ReadonlySet<string> = new Set([
  'item',
  'formState',
  'context',
]);

/**
 * Reserved interpolation roots that are not data source names. Excluded
 * from `referencedSources` so loading detection never flags them as
 * pending and so authors cannot accidentally collide with built-ins by
 * naming a `dataSources[<name>]`. Mirrors the DSM root shape from
 * `DataSourceManager.tsx#buildContextFromLiveData` plus `event` (action
 * resolver only).
 */
export const RESERVED_TEMPLATE_ROOTS: ReadonlySet<string> = new Set([
  'context',
  'formState',
  'topology',
  'item',
  'event',
  // `this` exposes the bound component's own props (today: `formKey`)
  // to `_action.*` params. Like the other reserved roots it must NOT
  // be treated as a data-source dependency, otherwise an action with
  // `params.keys: ["{{this.formKey}}"]` would phantom-flag the
  // component as `loading=true` until a non-existent `this` source
  // resolves.
  'this',
]);

/**
 * `route` and `scope` reserve their names only while the matching
 * provider is mounted. Without one, a data source of that name keeps
 * the `{{route.*}}` / `{{scope.*}}` reads it had before.
 */
export function activeProviderRoots(input: {
  readonly route?: unknown;
  readonly scope?: Readonly<Record<string, unknown>>;
}): ReadonlySet<string> {
  const names = new Set<string>();
  if (input.route !== undefined) {
    names.add('route');
  }
  if (input.scope !== undefined) {
    names.add('scope');
  }
  return names;
}

export function isReservedInterpolationRoot(
  name: string,
  providerRoots: ReadonlySet<string>,
): boolean {
  return RESERVED_TEMPLATE_ROOTS.has(name) || providerRoots.has(name);
}
